/**
 * Platform-admin shell — cross-tenant route subtree mounted on
 * ``admin.forgmatic.com``. Forgmatic staff use this shell to manage
 * the SaaS itself (Customers directory, Audit log, Feature flags).
 *
 * Org-admin pages (Orders, Pricing, Members…) are NOT mounted here
 * on purpose — staff that needs them switches to the relevant
 * tenant subdomain. The ``isPlatformShellHost`` gate inside
 * AdminLayout still 403s non-platform-admin users.
 */

import { Navigate, type RouteObject } from "react-router-dom";

import { withRouteErrorBoundary } from "@/components/error/withRouteErrorBoundary";

import {
  Account,
  AdminContactInbox,
  AdminMail,
  AdminContacts,
  AdminCustomerDetail,
  AdminNewsletter,
  AdminTemplates,
  AdminIntegrationsOverview,
  AdminLayout,
  AdminOperationGraph,
  AdminPageVisibilityRules,
  AdminMenuLayout,
  AdminPlans,
  AdminPlatformAuditLog,
  AdminPlatformCompanyProfile,
  AdminChangelog,
  AdminLegalDocuments,
  AdminSettingOverrides,
  AdminPlatformGroupDetail,
  AdminPlatformGroups,
  AdminPlatformLanguages,
  AdminPlatformLifecycleGraph,
  AdminPlatformMaterials,
  AdminPlatformMaterialCatalog,
  AdminPlatformArtifactProperties,
  AdminPlatformMachineProperties,
  AdminPlatformMachines,
  AdminPlatformOperationCategories,
  AdminPlatformOverview,
  AdminPlatformApps,
  AdminPlatformProcesses,
  AdminPlatformQuoteSettings,
  AdminPlatformCountries,
  AdminPlatformCurrencies,
  AdminCrm,
  AdminPlatformShipping,
  AdminPlatformUnits,
  AdminProviderRanking,
  AdminHelpAnnotations,
  AdminTenantDetail,
  AdminTenants,
  Lazy,
  NotFound,
  PlatformAdminModuleAdapters,
} from "./lazyPages";

export const platformAdminChildren: RouteObject[] = [
  {
    path: "admin",
    element: <Lazy><AdminLayout /></Lazy>,
    children: withRouteErrorBoundary([
      // Cross-tenant KPI dashboard — landing for platform staff.
      // The customer directory moved to ``/admin/customers`` so the
      // index can carry tenant + plan-tier + revenue counts that
      // staff actually look at on every login.
      { index: true, element: <Lazy><AdminPlatformOverview /></Lazy> },
      // Self-service account for the logged-in platform admin — reuses
      // the customer ``Account`` page verbatim. Linked from
      // ``AdminUserMenu`` (shared across both admin shells).
      { path: "account", element: <Lazy><Account /></Lazy> },
      { path: "contacts", element: <Lazy><AdminContacts /></Lazy> },
      // Detail kontaktu je modal nad seznamem — týž element, jiná adresa.
      { path: "contacts/:contactId", element: <Lazy><AdminContacts /></Lazy> },
      // KAN-1568: Obchod — detail firmy je modal nad nástěnkou na vlastní adrese.
      { path: "crm", element: <Lazy><AdminCrm /></Lazy> },
      { path: "crm/:companyId", element: <Lazy><AdminCrm /></Lazy> },
      // PR9 Kontakty SSoT: list žije na centrálních „Kontaktech";
      // v platform shellu zůstává jen detail kontaktu.
      { path: "customers/:userId", element: <Lazy><AdminCustomerDetail /></Lazy> },
      { path: "tenants", element: <Lazy><AdminTenants /></Lazy> },
      { path: "tenants/:orgId", element: <Lazy><AdminTenantDetail /></Lazy> },
      {
        path: "tenants/:orgId/operation-graph",
        element: <Lazy><AdminOperationGraph /></Lazy>,
      },
      { path: "plans", element: <Lazy><AdminPlans /></Lazy> },
      // Žebříček dodavatelů (KAN-1364) — dřív sekce vestavěné Kvality.
      { path: "provider-ranking", element: <Lazy><AdminProviderRanking /></Lazy> },
      {
        path: "platform-processes",
        element: <Lazy><AdminPlatformProcesses /></Lazy>,
      },
      {
        // Katalog aplikací (apps program F1) — platformní CRUD +
        // publish + kill-switch; tenant store žije v tenant shellu.
        path: "platform-apps",
        element: <Lazy><AdminPlatformApps /></Lazy>,
      },
      {
        // Sloučená „Materiály" — kategorie materiálů + registr vlastností
        // materiálů (per-process matice) na jedné stránce.
        path: "platform-materials",
        element: <Lazy><AdminPlatformMaterials /></Lazy>,
      },
      {
        // Katalog materiálů — sourozenec taxonomie výš. Dvě stránky,
        // jedno oprávnění (``platform-materials``): rozdělení je
        // navigační, ne bezpečnostní.
        path: "platform-material-catalog",
        element: <Lazy><AdminPlatformMaterialCatalog /></Lazy>,
      },
      {
        // Artifact I/O kontrakty — platform registr vlastností artefaktu
        // (ArtifactPropertyDefinition). Kontrakty operací na klíče odkazují
        // v tabu „Artefakt" detailu platform procesu.
        path: "platform-artifact-properties",
        element: <Lazy><AdminPlatformArtifactProperties /></Lazy>,
      },
      {
        // Canonical machine-property registry ("Vlastnosti strojů").
        path: "platform-machine-properties",
        element: <Lazy><AdminPlatformMachineProperties /></Lazy>,
      },
      {
        // „Katalog strojů" — the machine building block (KAN-1099). Distinct
        // from the registry above: that one groups PROPERTY KEYS across
        // operations, this one holds whole machines a tenant can pick.
        path: "platform-machines",
        element: <Lazy><AdminPlatformMachines /></Lazy>,
      },
      {
        path: "platform-operation-categories",
        element: <Lazy><AdminPlatformOperationCategories /></Lazy>,
      },
      {
        // „Jednotky" — platform unit-palette overview + CRUD (the single source
        // for every "Jednotka" field), grouped by dimension so convertible
        // units cluster. Same data as the inline UnitPresetManager modal.
        path: "platform-units",
        element: <Lazy><AdminPlatformUnits /></Lazy>,
      },
      {
        // „Doprava a termíny" — platformní verze tenantí stránky
        // (KAN-1101). Čtyři kroky místo pěti: termíny operací jsou
        // per tenant, takže krok 05 tu nemá co dělat.
        path: "platform-shipping",
        element: <Lazy><AdminPlatformShipping /></Lazy>,
      },
      {
        // „Země" — platformní katalog zemí (KAN-1449).
        path: "platform-countries",
        element: <Lazy><AdminPlatformCountries /></Lazy>,
      },
      {
        // „Měny" — platformní katalog měn (KAN-1451).
        path: "platform-currencies",
        element: <Lazy><AdminPlatformCurrencies /></Lazy>,
      },
      {
        // „Nápověda" — platform-managed inline help annotations (SSoT),
        // the content behind every <HelpNote> across the admin UI.
        path: "help-annotations",
        element: <Lazy><AdminHelpAnnotations /></Lazy>,
      },
      {
        path: "lifecycle-graph",
        element: <Lazy><AdminPlatformLifecycleGraph /></Lazy>,
      },
      {
        // KAN-1407: hromadné operace se spouštějí z výběru na stránce
        // Organizace; samostatná stránka zanikla.
        path: "bulk-ops",
        element: <Navigate to="../tenants" relative="path" replace />,
      },
      // Cross-tenant Integrations matrix — distinct from the per-
      // tenant ``/admin/integrations`` (that one lives under tenant
      // subdomains and is org-scoped).
      { path: "integrations", element: <Lazy><AdminIntegrationsOverview /></Lazy> },
      { path: "audit", element: <Lazy><AdminPlatformAuditLog /></Lazy> },
      {
        path: "page-visibility-rules",
        element: <Lazy><AdminPageVisibilityRules /></Lazy>,
      },
      { path: "menu-layout", element: <Lazy><AdminMenuLayout /></Lazy> },
      // H3 #6 PR-B — cross-tenant custom-module submission review queue.
      {
        path: "module-adapters",
        element: <Lazy><PlatformAdminModuleAdapters /></Lazy>,
      },
      // KAN-1690 — samostatný „Katalog dovedností" zrušen; limity operace
      // (``platform_skill_catalog``) se edituje jen na kartě operace, záložka
      // „Limity operace". Stará adresa drží záložky.
      {
        path: "skill-catalog",
        element: <Navigate to="../platform-processes" relative="path" replace />,
      },
      // Contact-inbox Kanban — mounted on the platform shell so
      // forgmatic-staff can triage messages submitted via the
      // platform landing form (``org_id IS NULL``).
      {
        path: "mail",
        element: <Lazy><AdminMail /></Lazy>,
      },
      {
        path: "contact-inbox",
        element: <Lazy><AdminContactInbox /></Lazy>,
      },
      { path: "platform-groups", element: <Lazy><AdminPlatformGroups /></Lazy> },
      {
        path: "platform-groups/:groupId",
        element: <Lazy><AdminPlatformGroupDetail /></Lazy>,
      },
      {
        path: "settings/templates",
        element: <Lazy><AdminTemplates /></Lazy>,
      },
      {
        // Newsletter — jedna komponenta, scope podle shellu.
        path: "newsletter",
        element: <Lazy><AdminNewsletter /></Lazy>,
      },
      // Původní samostatné stránky sloučeny — redirect kvůli bookmarkům.
      {
        path: "settings/email-templates",
        element: <Navigate to="../templates" relative="path" replace />,
      },
      {
        path: "settings/pdf-templates",
        element: <Navigate to="../templates" relative="path" replace />,
      },
      // „Profil firmy" — „Provozovatel" (legal entity) a „Branding"
      // (patička e-mailů, claim, odesílatel) splynuly do jedné stránky
      // se dvěma záložkami. Obě poloviny ukládají na JINÝ endpoint, ale
      // popisují jedno: jak se platforma prezentuje ven.
      {
        path: "settings/platform-company",
        element: <Lazy><AdminPlatformCompanyProfile /></Lazy>,
      },
      // Legacy routy drží bookmarky a míří na tu záložku, kterou
      // původní stránka představovala.
      {
        path: "settings/branding",
        element: (
          <Navigate
            to="../settings/platform-company?tab=appearance"
            replace
          />
        ),
      },
      // KAN-1365: kdo si přepsal kterou platformní výchozí hodnotu —
      // read-only, cross-tenant, proto jen v platform shellu.
      {
        path: "settings/setting-overrides",
        element: <Lazy><AdminSettingOverrides /></Lazy>,
      },
      // DPA se editovala tady, než se sloučila do právních dokumentů
      // (KAN-124). Routa zůstává jako přesměrování: odkaz na ni je
      // v záložkách, v e-mailech a v dokumentaci, a 404 by vypadala
      // jako by editor zmizel — on se jen přestěhoval.
      {
        path: "dpa",
        element: <Navigate to="../legal-documents" relative="path" replace />,
      },
      // Právní dokumenty — táž komponenta i v tenant shellu, jen s jiným
      // scope: tady se píší texty platformy a výchozí šablony.
      {
        path: "legal-documents",
        element: <Lazy><AdminLegalDocuments /></Lazy>,
      },
      // Redakce veřejného changelogu. Platform shell only — changelog
      // vydává platforma o sobě, tenant vlastní nemá.
      {
        path: "settings/changelog",
        element: <Lazy><AdminChangelog /></Lazy>,
      },
      // „Jazyky a překlady" — DB-backed language registry + platform-shell
      // translation editor (central i18n management).
      {
        path: "settings/languages",
        element: <Lazy><AdminPlatformLanguages /></Lazy>,
      },
      // „Veřejný web" splynul s kartou Překlady (KAN-1969). Routa zůstává
      // jako přesměrování kvůli záložkám — míří rovnou na texty webu.
      {
        path: "settings/public-site",
        element: (
          <Navigate
            to="../settings/languages?tab=translations&location=web"
            replace
          />
        ),
      },
      // Platform-singleton business-info editor (legal_name, IČO/DIČ,
      // billing) — dnes záložka „Firemní údaje" v „Profilu firmy".
      // Zůstává jako redirect kvůli bookmarkům. Pod tenant shellem je
      // na téže cestě pořád REÁLNÁ stránka: tam na ni míří onboarding
      // wizard a jeho guard redirect nesnese.
      {
        path: "settings/business",
        element: (
          <Navigate to="../settings/platform-company?tab=details" replace />
        ),
      },
      // KAN-1677 — „DPH a daně" je stránka OBCHODU (košík, země doručení,
      // sazby). Na platformě mířila na tenanta ``default`` (demo obchod);
      // plátcovství a DIČ provozovatele jsou teď v Profilu firmy.
      {
        path: "settings/vat",
        element: (
          <Navigate to="../settings/platform-company?tab=details" replace />
        ),
      },
      // Same dual-mount story as ``settings/business`` above — nav
      // surfaces this as a shared-scope page, so the route must
      // exist in the platform shell too. Edits the default tenant's
      // ``Setting(key="quote_settings")`` row.
      {
        path: "settings/platform-quote",
        element: <Lazy><AdminPlatformQuoteSettings /></Lazy>,
      },
      // 404 v admin zóně MUSÍ sedět pod AdminLayoutem: top-level
      // catch-all níže by sice cestu chytil, ale ShellFrame na
      // ``/admin/*`` skrývá Header+Footer, takže by se NotFound
      // vykreslil úplně bez chrome. Tady zůstává admin menu stát.
      { path: "*", element: <NotFound /> },
    ]),
  },
  // Bare ``/`` on admin.forgmatic.com redirects into /admin so
  // visitors don't land on a 404.
  { index: true, element: <Navigate to="admin" replace /> },
  { path: "*", element: <NotFound /> },
];
